/**
 * Die zehn Systemrollen eines Mandanten — die eine Quelle für Rollenname und
 * Lizenzart, gemeinsam genutzt von Frontend und Server.
 *
 * Der Onboarding-Wizard zeigt sie mit Beispielpositionen und Beschreibungstext
 * (src/features/onboarding/config.ts#ROLE_DESCRIPTIONS, prüft die Deckungs-
 * gleichheit im Test). Die Provisionierung bildet die im Onboarding erfassten
 * Benutzerrollen hierauf ab (server/routes/tenants.js) und leitet daraus die
 * belegte Lizenzart ab.
 *
 * Reihenfolge = Anzeigereihenfolge im Wizard, von den weitreichendsten Büro-
 * rechten zu den eingeschränktesten. Nicht umsortieren.
 */
export const SYSTEM_ROLES = [
  { name: "Administrator", license: "Keine Lizenz benötigt" },
  { name: "Operator & Administrator", license: "Office-Lizenz" },
  { name: "Operator", license: "Office-Lizenz" },
  { name: "Disponent", license: "Office-Lizenz" },
  { name: "Mobiler Erfasser", license: "Mobile-Lizenz" },
  { name: "Bearbeiter/Disponent", license: "Mobile-Lizenz" },
  { name: "Selbstverwalter", license: "Mobile-Lizenz" },
  { name: "Beobachter", license: "Office-Lizenz" },
  { name: "Basic", license: "Zeiterfassungs-Lizenz" },
  { name: "Kundennutzer", license: "Kunden-Lizenz" }
];

export const SYSTEM_ROLE_NAMES = SYSTEM_ROLES.map((role) => role.name);

/**
 * Die Rollen, für die die Fahrer-App gedacht ist (S4.1: „Route und Guard für
 * Rolle Mobiler Erfasser / Selbstverwalter / Bearbeiter-Disponent") — genau die
 * drei Systemrollen mit Mobile-Lizenz.
 *
 * Abgeleitet statt danebengeschrieben, aus demselben Grund wie
 * SYSTEM_ROLE_NAMES: eine zweite Handliste liefe irgendwann auseinander. Dass
 * die Ableitung genau die drei von S4.1 genannten Namen trifft, hält
 * shared/roles.test.js fest — ändert jemand eine Lizenzart, schlägt das dort an
 * und nicht erst beim Fahrer, der die App nicht mehr öffnen kann.
 *
 * Das ist eine Zuständigkeit für die *Oberfläche*, keine Berechtigung: was eine
 * Rolle an einem Auftrag tun darf, entscheidet weiterhin allein
 * server/orders/transitions.js.
 */
const MOBILE_LICENSE = "Mobile-Lizenz";

export const MOBILE_ROLES = Object.freeze(SYSTEM_ROLES.filter((role) => role.license === MOBILE_LICENSE).map((role) => role.name));

export function usesMobileApp(role) {
  return MOBILE_ROLES.includes(role);
}

/**
 * Der Onboarding-Kontakt (Firmendaten) wird bei der Provisionierung das erste
 * Mitglied und bekommt immer die Administrator-Rolle (SESSIONS.md S1.2).
 */
export const ONBOARDING_CONTACT_ROLE = "Administrator";

/**
 * Trägt ein im Onboarding erfasster Benutzer keine bekannte Rolle, wird er als
 * Beobachter angelegt: er kann sehen, aber nichts auslösen — die sichere
 * Vorgabe, bis ihn jemand im Mandanten bewusst hochstuft.
 */
export const DEFAULT_SYSTEM_ROLE = "Beobachter";

/** Lizenzart einer Systemrolle; leerer String, wenn die Rolle unbekannt ist. */
export function licenseForSystemRole(name) {
  return SYSTEM_ROLES.find((role) => role.name === name)?.license ?? "";
}

/**
 * Rollen, die Mitglieder eines Mandanten anlegen, ändern, löschen und einladen
 * dürfen (S1.4). Vor dieser Session prüfte `server/routes/masterdata.js` gar
 * keine Rolle — jedes Mitglied konnte sich selbst zum Administrator machen.
 *
 * Bewusst nur die beiden Administratorrollen und bewusst nur für Mitglieder:
 * die übrigen Stammdaten (Fahrzeuge, Adressen, Auftragsarten) bleiben für alle
 * Mitglieder pflegbar, weil S1.3 ausdrücklich die Selbstbedienung des Mandanten
 * ist. Wer dort welche Rolle braucht, ist eine eigene Frage — sie steht als
 * offener Punkt in STATUS.md.
 */
const MEMBER_ADMIN_ROLES = Object.freeze(["Administrator", "Operator & Administrator"]);

export function canManageMembers(role) {
  return MEMBER_ADMIN_ROLES.includes(role);
}
